/**
 * Persists the dine-in payer token across reloads. Mirrors
 * `participant-token.ts`, with two deliberate differences:
 *
 *  - localStorage instead of sessionStorage: a diner may background the
 *    browser mid-meal and come back in a fresh tab; the token is scoped to
 *    one orderId and short-TTL (~6h server-side), so persistence is safe.
 *  - Expiry-aware: the JWT `exp` claim is decoded locally so a stale token
 *    is dropped client-side instead of producing a confusing 401 → the
 *    PayerNameSheet simply re-appears and a new session is minted.
 *
 * The payer token is issued by
 * `POST /v1/online-ordering/branches/{branchId}/tables/{tableId}/payer-session`
 * and is tied to (tenantId, orderId, guestId) — spec §4. Losing it just
 * means re-entering a name.
 *
 * Device fingerprint: reuse `deviceFingerprint()` from participant-token.ts
 * (same uuid the group-cart join flow uses) — do NOT mint a second one.
 */

import { scopedStorageKey } from "./storageScope";

const PAYER_KEY = "tappya_payer_token_v1";

export interface StoredPayer {
  payerToken: string;
  guestId: string;
  orderId: string;
  displayName: string;
}

function isStoredPayer(p: unknown): p is StoredPayer {
  if (!p || typeof p !== "object") return false;
  const r = p as Record<string, unknown>;
  return (
    typeof r.payerToken === "string" &&
    typeof r.guestId === "string" &&
    typeof r.orderId === "string" &&
    typeof r.displayName === "string"
  );
}

/** Decode the JWT `exp` claim (ms since epoch), or null if undecodable. */
function tokenExpiryMs(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function readPayer(): StoredPayer | null {
  try {
    const raw = localStorage.getItem(scopedStorageKey(PAYER_KEY));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isStoredPayer(parsed)) return null;
    const exp = tokenExpiryMs(parsed.payerToken);
    if (exp !== null && exp <= Date.now()) {
      clearPayer();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** The stored payer, but only when it belongs to `orderId`. */
export function payerForOrder(orderId: string | undefined | null): StoredPayer | null {
  if (!orderId) return null;
  const p = readPayer();
  return p && p.orderId === orderId ? p : null;
}

export function writePayer(p: StoredPayer | null): void {
  try {
    const k = scopedStorageKey(PAYER_KEY);
    if (p) localStorage.setItem(k, JSON.stringify(p));
    else localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

export function clearPayer(): void {
  writePayer(null);
}
